import {
  CREDENTIAL_STATUS,
  CanalType,
  CodeName,
  CredentialStatus,
  ProductOfInterest,
  Prospect,
  ProspectDetails,
  ProspectLegalEntity,
  ProspectStatus,
  ProspectType,
} from "@/core/customers/entities/ProspectsTypes";
import { Gender } from "../entities";

// Shape de l'API list prospects (items)
export interface ApiProspectItem {
  id: string;
  first_name: string;
  last_name: string;
  /** Raison sociale (personne morale uniquement, `null` sinon). */
  companyName?: string | null;
  phone: string;
  /** Segment du dossier, rendu par le back : PERSONNE_PHYSIQUE ou PERSONNE_MORALE. */
  type?: ProspectType;
  business_key: string;
  products: ProductOfInterest;
  status: ProspectStatus;
  branch: string;
  advisor: string;
  createdTimestamp: string;
  credentialStatus?: string | null;
  credential_status?: string | null;
  onboardingChannel?: CanalType | null;
}

export interface Nationality {
  id: number;
  code: string;
  label: string;
}

export interface GenericCodeLabel {
  id: number;
  code: string;
  label: string;
  description?: string | null;
  type?: {
    id: number;
    code: string;
    label: string;
  } | null;
}

export interface ApiLegalEntitySummary {
  companyName?: string | null;
  legalFormCode?: string | null;
  legalFormLabel?: string | null;
  incorporationCountryCode?: string | null;
  incorporationCountryName?: string | null;
  rccm?: string | null;
  sectorActivityCode?: string | null;
  sectorActivityLabel?: string | null;
  shareCapital?: number | null;
  incorporationDate?: string | null;
  headOfficeAddress?: string | null;
  corporatePurpose?: string | null;
  tradeName?: string | null;
  city?: string | null;
  authorizationNumber?: string | null;
  fiscalYearStart?: string | null;
  fiscalYearEnd?: string | null;
  annualRevenue?: number | null;
  consolidatedRevenue?: number | null;
  netResult?: number | null;
  totalAssets?: number | null;
  companyEmail?: string | null;
  companyPhone?: string | null;
}

export interface ApiProspectDetailsItem {
  business_key: string;
  first_name: string;
  last_name: string;
  type: ProspectType;
  /** Identité de l'entreprise (personne morale uniquement, `null` sinon). */
  legalEntity?: ApiLegalEntitySummary | null;
  phone?: string | null;
  phone1?: string | null;
  phone1CountryCode: string | null;
  phone1Verified: boolean;
  phone2: string | null;
  phone2CountryCode: string | null;
  phone2Verified: boolean;
  phone3?: string | null;
  phone3CountryCode?: string | null;
  phone3Verified?: boolean;
  phone4?: string | null;
  phone4CountryCode?: string | null;
  phone4Verified?: boolean;
  fax?: string | null;
  products: string[];

  civility: CodeName | null;
  gender: Gender | null;

  // doublons frontend
  firstName: string;
  lastName: string;

  birthDate: string | null; // ISO date
  birthPlace: string | null;

  nationality: CodeName | null;
  email?: string | null;
  email1?: string | null;
  email2?: string | null;
  email1Verified: boolean;
  email2Verified: boolean;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  postal_address?: string | null;
  postalAddress?: string | null;
  since?: string | null;
  since_at?: string | null;

  country_residence: CodeName | null;
  family_status: CodeName | null;

  profession: string | null;
  professionCode?: string | null;
  monthly_income: number | null;
  sector_activity: string | null;
  qualityCode?: string | null;
  quality_code?: string | null;

  profession_category: CodeName | null;
  paysProfession?: CodeName | null;
  profession_country?: CodeName | null;

  peps: boolean | null;
  tax_id_number?: string | null;
  taxIdNumber?: string | null;
  resident: boolean;

  number_children: number | null;
  comment: string | null;
  motif?: string | null;
  contactForSavingCardCredit: boolean | null;
  idPaper: {
    id: string | null;
    type: string | null;
    idPaperNumber: string | null;
    deliveryDate: string | null;
    validityDate: string | null;
    deliveryPlace: string | null;
    organisationWhichDeliver: string | null;
  };
  fatherFirstName?: string | null;
  fatherFirstNames?: string | null;
  fatherLastName?: string | null;
  motherFirstName?: string | null;
  motherFirstNames?: string | null;
  motherMaidenName?: string | null;
  spouseFirstName?: string | null;
  spouseLastName?: string | null;
  otherBankRelation?: boolean | null;
  bankPersonnel?: boolean | null;
  syncStatus?: string | null;
  syncErrorMessage?: string | null;
  lastCbsSyncAt?: string | null;
  credentialStatus?: string | null;
  credential_status?: string | null;
}

export interface Demand {
  id: string;
  demandParams: unknown | null;
  segment: "PP" | "PM" | string;
  canal: "WEB" | "MOBILE" | "BRANCH" | "CORPORATE" | string;
  pays: GenericCodeLabel;
}

export interface CustomerDemand {
  id: string;
  customer: ApiProspectDetailsItem;
  demand: Demand;
}

// export interface ApiProspectByBusinessKeyResponse {
//   business_key: string;
//   first_name: string;
//   last_name: string;
//   phone: string;
//   produits: string | null;
//   civility: string | null;
//   gender: string | null;
//   firstName: string;
//   lastName: string;
//   birthDate: string | null;
//   birthPlace: string | null;
//   nationality: string | null;
//   email: string | null;
//   address: string | null;
//   city: string | null;
//   postal_code: string | null;
//   country_residence: string | null;
//   family_status: string | null;
//   profession: string | null;
//   monthly_income: string | null;
//   sector_activity: string | null;
//   number_children: number | null;
// }

export interface ApiIdPaper {
  id: string;
  type: string;
  idPaperNumber: string;
  nni?: string | null;
  deliveryDate: string;
  validityDate: string;
  deliveryPlace: string;
  organisationWhichDeliver: string | null;
  issuing_country?: { code: string; name: string } | null;
  issuingCountry?: { code: string; name: string } | null;
  document_reference?: string | null;
  documentReference?: string | null;
}

export type ApiProspectByBusinessKeyResponse = {
  business_key: string;
  customerCode?: string | null;
  first_name: string;
  last_name: string;
  type?: "PERSONNE_PHYSIQUE" | "PERSONNE_MORALE";

  phone: string | null;
  phone1: string | null;
  phone1CountryCode: string | null;
  phone1Verified: boolean;
  phone2: string | null;
  phone2CountryCode: string | null;
  phone2Verified: boolean;
  phone3?: string | null;
  phone3CountryCode?: string | null;
  phone3Verified?: boolean;
  phone4?: string | null;
  phone4CountryCode?: string | null;
  phone4Verified?: boolean;
  fax?: string | null;
  /** API may send products (array) or produits (legacy) */
  products?: string[];
  produits?: string | null;

  civility: CodeName | null;
  gender: "MALE" | "FEMALE" | null;

  firstName?: string;
  lastName?: string;

  birthDate: string | null;
  birthPlace: string | null;
  nationality: CodeName | null;

  email: string | null;
  email1: string | null;
  email1Verified: boolean;
  email2: string | null;
  email2Verified: boolean;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  postal_address?: string | null;
  postalAddress?: string | null;
  since?: string | null;
  since_at?: string | null;

  country_residence: CodeName | null;
  family_status: CodeName | null;
  legal_capacity?: CodeName | null;
  legal_situation?: CodeName | null;
  legal_referent?: string | null;
  legal_representative?: string | null;
  fatherLastName?: string | null;
  motherMaidenName?: string | null;
  fatherFirstName?: string | null;
  motherFirstName?: string | null;
  death_date?: string | null;
  matrimonial_regime?: CodeName | null;
  marriage_contract?: boolean | null;
  spouseLastName?: string | null;
  spouseFirstName?: string | null;
  otherBankRelation?: boolean | null;
  bankPersonnel?: boolean | null;
  profession_country?: CodeName | null;
  paysProfession?: CodeName | string | null;
  employer?: string | null;

  profession: string | null;
  monthly_income: number | null;
  sector_activity: string | null;
  qualityCode?: string | null;
  quality_code?: string | null;
  profession_category: CodeName | null;

  peps: boolean | null;
  tax_id_number?: string | null;
  taxIdNumber?: string | null;
  resident: boolean;
  number_children: number | null;
  comment: string | null;
  contactForSavingCardCredit: boolean | null;

  patrimoine_real_estate_count?: string | null;
  patrimoineRealEstateCount?: string | null;
  patrimoine_real_estate_assets?: string[] | string | null;
  patrimoineRealEstateAssets?: string[] | string | null;
  patrimoine_real_estate_insured?: boolean | null;
  patrimoineRealEstateInsured?: boolean | null;
  patrimoine_real_estate_estimated_value?: string | null;
  patrimoineRealEstateEstimatedValue?: string | null;
  patrimoine_other_bank_accounts?: string[] | string | null;
  patrimoineOtherBankAccounts?: string[] | string | null;
  patrimoine_other_bank_accounts_value?: string | null;
  patrimoineOtherBankAccountsValue?: string | null;
  patrimoine_valuable_objects?: string[] | string | null;
  patrimoineValuableObjects?: string[] | string | null;
  patrimoine_valuables_insured?: boolean | null;
  patrimoineValuablesInsured?: boolean | null;
  patrimoine_valuables_value?: string | null;
  patrimoineValuablesValue?: string | null;

  situationPatrimoniale?: string | null;
  situation_patrimoniale?: string | null;
  sourceDesFonds?: string | null;
  source_des_fonds?: string | null;
  objetRelationBancaire?: string | null;
  objet_relation_bancaire?: string | null;
  residenceFiscalePays?: string | null;
  residence_fiscale_pays?: string | null;
  ppe?: boolean | null;
  fonctionPpeExercee?: string | null;
  fonction_ppe_exercee?: string | null;
  beneficiaireEffectif?: boolean | null;
  beneficiaire_effectif?: boolean | null;
  identiteBeneficiaireEffectif?: string | null;
  identite_beneficiaire_effectif?: string | null;

  idPaper?: ApiIdPaper | null;

  /** Statut de risque renvoyé par le backend (fiche client). */
  risk_status?: string | null;
  /** URL signée du portrait du client (fiche client). */
  portrait_url?: string | null;

  /** Agence gestionnaire du dossier (code CBS). */
  agencyCode?: string | null;
  /** Agence gestionnaire du dossier (libellé). */
  agencyName?: string | null;
  /** Login (matricule) du conseiller qui a créé le dossier. */
  advisorLogin?: string | null;
  /** Nom complet du conseiller qui a créé le dossier. */
  advisorName?: string | null;
};

function mapCredentialStatus(
  status?: string | null,
): CredentialStatus | undefined {
  if (!status) return undefined;

  const allowed: CredentialStatus[] = Object.values(CREDENTIAL_STATUS);

  return allowed.includes(status as CredentialStatus)
    ? (status as CredentialStatus)
    : undefined;
}

export class ProspectsPipelineMapper {
  static toDomainItem(item: ApiProspectItem): Prospect {
    return {
      id: item.id,
      first_name: item.first_name,
      last_name: item.last_name,
      companyName: item.companyName ?? null,
      business_key: item.business_key,
      // Le back rend le segment sur chaque ligne ; ne pas le transporter privait la colonne
      // Segment de sa seule source, et toute personne morale s'affichait comme une PP.
      type: item.type,
      phone: item.phone,
      productOfInterest: item.products,
      status: item.status ?? "PROSPECT",
      branch: item.branch,
      advisor: item.advisor,
      createdTimestamp: item.createdTimestamp,
      credentialStatus: mapCredentialStatus(
        item.credentialStatus ?? item.credential_status,
      ),
      onboardingChannel: item.onboardingChannel ?? null,
    };
  }

  // Segment inconnu : le marqueur de valeur vide, et non "Personne physique". Retomber sur PP
  // affirmait un segment que la donnee ne porte pas - la meme confusion que la colonne Segment,
  // qui rendait "PP" en dur et affichait ainsi toute personne morale comme une PP.
  static typeMap(t?: string | null) {
    if (t === "PERSONNE_PHYSIQUE") return "Personne physique";
    if (t === "PERSONNE_MORALE") return "Personne morale";
    return "-";
  }

  static toDomainItems(items: ApiProspectItem[]): Prospect[] {
    return (items || []).map(this.toDomainItem);
  }

  static toDomainFromBusinessKey(item: ApiProspectDetailsItem): Prospect {
    const spouseFirstName =
      item.spouseFirstName ?? item.spouseFirstName ?? null;
    const spouseLastName = item.spouseLastName ?? item.spouseLastName ?? null;

    return {
      id: item.business_key,
      business_key: item.business_key,
      type: item.type,
      first_name: item.first_name,
      last_name: item.last_name,
      phone: item.phone1 ?? item.phone ?? "",
      phone1: item.phone1 ?? item.phone ?? null,
      phone1CountryCode: item.phone1CountryCode ?? null,
      phone1Verified: item.phone1Verified ?? false,
      phone2: item.phone2 ?? null,
      phone2CountryCode: item.phone2CountryCode ?? null,
      phone2Verified: item.phone2Verified ?? false,
      phone3: item.phone3 ?? null,
      phone3CountryCode: item.phone3CountryCode ?? null,
      phone3Verified: item.phone3Verified ?? false,
      phone4: item.phone4 ?? null,
      phone4CountryCode: item.phone4CountryCode ?? null,
      phone4Verified: item.phone4Verified ?? false,
      fax: item.fax ?? null,
      productOfInterest: item.products[0] ?? null,
      products: item.products ?? [],
      status: "PROSPECT",
      email: item.email1 ?? item.email ?? "",
      email1: item.email1 ?? item.email ?? null,
      email1Verified: item.email1Verified ?? false,
      email2: item.email2 ?? null,
      email2Verified: item.email2Verified ?? false,
      civilityCode: item.civility?.code ?? null,
      civility: item.civility ?? null,
      gender: item.gender,
      birthDate: item.birthDate ?? null,
      birthPlace: item.birthPlace ?? null,
      nationalityCode: item.nationality?.code ?? null,
      nationality: item.nationality ?? null,
      address: item.address,
      city: item.city,
      postalCode: item.postal_code,
      countryResidenceCode: item.country_residence?.code ?? null,
      countryResidence: item.country_residence ?? null,
      familyStatusCode: item.family_status?.code ?? null,
      familyStatus: item.family_status ?? null,
      professionCategory: item.profession_category ?? null,
      professionCode: item.professionCode ?? null,
      profession: item.profession,
      professionCountry: item.profession_country ?? item.paysProfession ?? null,
      monthlyIncome: item.monthly_income ?? undefined,
      sectorActivity: item.sector_activity,
      qualityCode: item.qualityCode ?? item.quality_code ?? null,
      peps: item.peps ?? undefined,
      taxIdNumber: item.taxIdNumber ?? item.tax_id_number ?? null,
      resident: item.resident,
      numberChildren: item.number_children ?? 0,
      comment: item.comment ?? undefined,
      contactForSavingCardCredit: item.contactForSavingCardCredit ?? undefined,
      issueDate: item.idPaper?.deliveryDate ?? undefined,
      expireDate: item.idPaper?.validityDate ?? undefined,
      issueCountry:
        item.idPaper?.deliveryPlace ??
        item.idPaper?.organisationWhichDeliver ??
        undefined,
      documentNumber: item.idPaper?.idPaperNumber ?? undefined,
      spouseFirstName,
      spouseLastName,
      partnerFirstName: spouseFirstName ?? undefined,
      partnerLastName: spouseLastName ?? undefined,
      fatherLastName: item.fatherLastName ?? undefined,
      fatherFirstName:
        item.fatherFirstName ?? item.fatherFirstNames ?? undefined,
      motherMaidenName: item.motherMaidenName ?? undefined,
      motherFirstName:
        item.motherFirstName ?? item.motherFirstNames ?? undefined,
      otherBankRelation:
        item.otherBankRelation ?? item.otherBankRelation ?? null,
      bankPersonnel: item.bankPersonnel ?? item.bankPersonnel ?? null,
      syncStatus: item.syncStatus ?? null,
      syncErrorMessage: item.syncErrorMessage ?? null,
      lastCbsSyncAt: item.lastCbsSyncAt ?? null,
      credentialStatus: mapCredentialStatus(
        item.credentialStatus ?? item.credential_status,
      ),
    };
  }
}

export class ProspectsPipelineMapperDetails {
  private static mapProspectType(type?: string | null): ProspectType {
    if (type === "PERSONNE_MORALE") return "PERSONNE_MORALE";
    return "PERSONNE_PHYSIQUE";
  }

  static toDomainFromProspectDetails(
    customer: ApiProspectDetailsItem,
  ): ProspectDetails {
    const idPaperType =
      customer.idPaper?.type === "PASSPORT" || customer.idPaper?.type === "CNI"
        ? customer.idPaper.type
        : undefined;

    return {
      id: customer.business_key,
      businessKey: customer.business_key,
      firstName: customer.first_name ?? customer.firstName ?? "",
      lastName: customer.last_name ?? customer.lastName ?? "",
      type: this.mapProspectType(customer.type),
      legalEntity: customer.legalEntity
        ? ({
            companyName: customer.legalEntity.companyName ?? null,
            legalFormCode: customer.legalEntity.legalFormCode ?? null,
            legalFormLabel: customer.legalEntity.legalFormLabel ?? null,
            incorporationCountryCode:
              customer.legalEntity.incorporationCountryCode ?? null,
            incorporationCountryName:
              customer.legalEntity.incorporationCountryName ?? null,
            rccm: customer.legalEntity.rccm ?? null,
            sectorActivityCode: customer.legalEntity.sectorActivityCode ?? null,
            sectorActivityLabel: customer.legalEntity.sectorActivityLabel ?? null,
            shareCapital: customer.legalEntity.shareCapital ?? null,
            incorporationDate: customer.legalEntity.incorporationDate ?? null,
            headOfficeAddress: customer.legalEntity.headOfficeAddress ?? null,
            corporatePurpose: customer.legalEntity.corporatePurpose ?? null,
            tradeName: customer.legalEntity.tradeName ?? null,
            city: customer.legalEntity.city ?? null,
            authorizationNumber: customer.legalEntity.authorizationNumber ?? null,
            fiscalYearStart: customer.legalEntity.fiscalYearStart ?? null,
            fiscalYearEnd: customer.legalEntity.fiscalYearEnd ?? null,
            annualRevenue: customer.legalEntity.annualRevenue ?? null,
            consolidatedRevenue: customer.legalEntity.consolidatedRevenue ?? null,
            netResult: customer.legalEntity.netResult ?? null,
            totalAssets: customer.legalEntity.totalAssets ?? null,
            companyEmail: customer.legalEntity.companyEmail ?? null,
            companyPhone: customer.legalEntity.companyPhone ?? null,
          } as ProspectLegalEntity)
        : null,
      email: customer.email1 ?? customer.email ?? undefined,
      phone: customer.phone1 ?? customer.phone ?? "",
      phoneCountryCode: customer.phone1CountryCode ?? null,
      phoneVerified: customer.phone1Verified ?? false,
      country: customer.country_residence?.name ?? "",
      // Ce chemin ne porte pas le canal : vide plutot qu'une valeur inventee, l'ecran affiche
      // alors son marqueur de valeur absente au lieu d'un canal faux.
      canal: "",
      screening: "NON_SCREENÉ",
      status: "PROSPECT",
      lastUpdate: customer.lastCbsSyncAt ?? "",
      onboarding: false,
      notes: customer.comment ?? undefined,
      motif: customer.motif ?? undefined,
      birthDate: customer.birthDate ?? "",
      birthPlace: customer.birthPlace ?? undefined,
      IdDocType: idPaperType,
      // IdDocNumber:
      //   customer.idPaper?.idPaperNumber ?? customer.documentNumber ?? undefined,
      // expiryDate:
      //   customer.idPaper?.validityDate ?? customer.expireDate ?? undefined,
      // deliveredDate:
      //   customer.idPaper?.deliveryDate ?? customer.dateOfIssue ?? undefined,
      address1: customer.address ?? undefined,
      address2: undefined,
      city: customer.city ?? undefined,
      timeline: [],
      nationality: customer.nationality
        ? {
            id: null,
            code: customer.nationality.code,
            label: customer.nationality.name,
          }
        : undefined,
      gender: customer.gender ?? undefined,
      postalCode: customer.postal_code ?? undefined,
      comment: customer.comment ?? undefined,
      contactForSavingCardCredit:
        customer.contactForSavingCardCredit ?? undefined,
      mounthlyIncome: customer.monthly_income ?? undefined,
      peps: customer.peps ?? undefined,
      productsOfInterest: customer.products ?? undefined,
      credentialStatus: mapCredentialStatus(
        customer.credentialStatus ?? customer.credential_status,
      ),
    };
  }

  static toDomainFromDemand(payload: CustomerDemand): ProspectDetails {
    const { customer, demand } = payload;
    const mapTypeFromSegment = (seg?: string | null) => {
      if (seg === "PP") return "PERSONNE_PHYSIQUE" as const;
      if (seg === "PM") return "PERSONNE_MORALE" as const;
      return "PERSONNE_PHYSIQUE" as const;
    };
    // const mapStatus = (s?: string | null) => {
    //   if (s === "PROSPECT" || s === "CLIENT" || s === "BLOQUÉ") return s;
    //   return "PROSPECT" as const;
    // };
    return {
      id: customer.business_key,
      businessKey: customer.business_key,
      firstName: customer.first_name ?? "",
      lastName: customer.last_name ?? "",
      type:
        customer.type === "PERSONNE_PHYSIQUE" ||
        customer.type === "PERSONNE_MORALE"
          ? customer.type
          : mapTypeFromSegment(demand?.segment),
      email: customer.email1 ?? customer.email ?? undefined,
      phone: customer.phone1 ?? customer.phone ?? "",
      phoneCountryCode: customer.phone1CountryCode ?? null,
      phoneVerified: customer.phone1Verified ?? false,
      country: customer.country_residence?.name ?? "",
      canal: (demand?.canal ?? "") as CanalType,
      screening: "NON_SCREENÉ",
      status: "PROSPECT",
      lastUpdate: "",
      onboarding: false,
      notes: undefined,
      birthDate: "",
      birthPlace: undefined,
      // IdDocType: customer.idPaper?.type ?? undefined,
      // IdDocNumber: customer.idPaper?.idPaperNumber ?? undefined,
      // expiryDate: customer.idPaper?.validityDate ?? undefined,
      // deliveredDate: customer.idPaper?.deliveryDate ?? undefined,
      address1: customer.address ?? undefined,
      city: customer.city ?? undefined,
      timeline: [],
      nationality: {
        code: customer.nationality?.code ?? "",
        label: customer.nationality?.name ?? "",
      },
      gender: customer.gender ?? undefined,
      // postalCode: customer.address?.geoLocation?.postalCode,
      contryOfApplication: {
        id: demand?.pays?.id ?? null,
        code: demand?.pays?.code ?? "",
        label: demand?.pays?.label ?? "",
      },
      comment: customer.comment ?? undefined,
      contactForSavingCardCredit:
        customer.contactForSavingCardCredit ?? undefined,
      productsOfInterest: customer.products ?? undefined,
    };
  }

  static toDomainItemsFromDemands(items: CustomerDemand[]): ProspectDetails[] {
    return (items || []).map(this.toDomainFromDemand);
  }
}
